// Make changes › When it changes — sentences ⇄ the designer's ItemChangeRule.
//
// "When someone changes <this field>, also: fill in X with Y from the picked
// record / set X to <this> × Z / set X to “value” / clear X / lock X when
// <this> is “value”." Each sentence compiles to one entry of the column's
// `item_change` rule (mode 'rule'): a `fill` mapping (lookup `table_col` or an
// `expr` in the engine's expression language) or a `protect` entry. Reading
// back is the exact inverse; anything these sentences cannot express (code
// mode, a hand-written expression, an advanced match) is returned as
// `unreadable` lines the panel shows read-only, and is kept untouched.

import type { ItemChange, ItemChangeFillMapping, ItemChangeProtectMapping, ItemChangeRule } from '../../txn/types'
import { parseLookup, type LookupSpec } from './fieldKinds'

export type CalcOp = '*' | '+' | '-' | '/'

export type IcSentence =
  | { type: 'fill'; target: string; source: string }
  | { type: 'calc'; target: string; op: CalcOp; field?: string; number?: string }
  | { type: 'set'; target: string; value: string }
  | { type: 'clear'; target: string }
  | { type: 'lock'; target: string; value: string }

export const CALC_OPS: { op: CalcOp; label: string }[] = [
  { op: '*', label: '×' }, { op: '+', label: '+' }, { op: '-', label: '−' }, { op: '/', label: '÷' },
]

/** How a value is written inside an expression: a text literal the engine's
 *  expression language reads back exactly ('…', or "…" when the value has an
 *  apostrophe; a backslash escapes). */
export function quoteText(v: string): string {
  const t = String(v).replace(/\\/g, '\\\\')
  return t.includes("'") ? `"${t.replace(/"/g, '\\"')}"` : `'${t}'`
}
/** A text literal, as a regular-expression source (one capture-free group). */
export const TEXT_LIT = `(?:'(?:[^'\\\\]|\\\\.)*'|"(?:[^"\\\\]|\\\\.)*")`
/** The value of a text literal written by quoteText; null when it is not one. */
export function unquoteText(q: string): string | null {
  if (!new RegExp(`^${TEXT_LIT}$`).test(q)) return null
  return q.slice(1, -1).replace(/\\(.)/g, '$1')
}
const unquote = unquoteText
const ref = (name: string) => `{form.${name}}`
const IDENT = '[A-Za-z_][A-Za-z0-9_]*'
const CALC_RE = new RegExp(`^\\{form\\.(${IDENT})\\}\\s*([*+\\-/])\\s*(?:\\{form\\.(${IDENT})\\}|(-?\\d+(?:\\.\\d+)?))$`)
const LOCK_RE = new RegExp(`^\\{form\\.(${IDENT})\\}\\s*==\\s*(${TEXT_LIT})$`)

export interface IcRead {
  sentences: IcSentence[]
  /** What the sentences cannot show (kept as it is, shown read-only). */
  unreadable: string[]
  /** The raw entries behind `unreadable`, kept untouched on write. */
  keep: { fill: ItemChangeFillMapping[]; protect: ItemChangeProtectMapping[] }
  /** Not a rule at all (business logic / Call API / code): its summary. */
  other: string | null
}

const lc = (s: string) => s.toLowerCase()

/** Read a column's item_change back into sentences. `self` = the column's
 *  stored name. */
export function readItemChange(item: ItemChange | string | null | undefined, self: string): IcRead {
  const out: IcRead = { sentences: [], unreadable: [], keep: { fill: [], protect: [] }, other: null }
  if (!item) return out
  if (typeof item === 'string') { out.other = item; return out }
  if (item.mode !== 'rule') {
    out.other = item.mode === 'business_logic'
      ? `Runs ${item.business_logic?.method || 'a function'}`
      : item.mode === 'rest_api' ? 'Calls an outside service' : 'Runs code'
    return out
  }
  const rule = item as ItemChangeRule
  if ((rule.match_mode && rule.match_mode !== 'simple') || (rule.where_sql || '').trim()) {
    out.unreadable.push('Looks up the picked record in its own way')
  }
  for (const f of rule.fill || []) {
    const target = String(f.form_col || '')
    if ((f.table_col || '').trim()) {
      out.sentences.push({ type: 'fill', target, source: String(f.table_col) })
      continue
    }
    const expr = String(f.expr || '').trim()
    const lit = unquote(expr)
    if (lit !== null) {
      out.sentences.push(lit === '' ? { type: 'clear', target } : { type: 'set', target, value: lit })
      continue
    }
    const m = expr.match(CALC_RE)
    if (m && lc(m[1]) === lc(self)) {
      out.sentences.push({ type: 'calc', target, op: m[2] as CalcOp, ...(m[3] ? { field: m[3] } : { number: m[4] }) })
      continue
    }
    out.unreadable.push(`Sets ${target} to ${expr}`)
    out.keep.fill.push(f)
  }
  for (const p of rule.protect || []) {
    const when = String(p.when || '').trim()
    const m = when.match(LOCK_RE)
    const v = m ? unquote(m[2]) : null
    if (m && v !== null && lc(m[1]) === lc(self)) {
      out.sentences.push({ type: 'lock', target: String(p.form_col), value: v })
    } else {
      out.unreadable.push(`Locks ${p.form_col} when ${when}`)
      out.keep.protect.push(p)
    }
  }
  return out
}

/** Compile sentences back into the column's item_change. What the previous
 *  rule held that the sentences cannot express is kept as it was, and so are
 *  its match options. Null when nothing is left. */
export function writeItemChange(opts: {
  self: string
  sentences: IcSentence[]
  previous: ItemChange | string | null | undefined
  /** The field's "picked from" screen (for fill sentences). */
  lookup: LookupSpec | string | null | undefined
}): ItemChangeRule | null {
  const prev = (opts.previous && typeof opts.previous === 'object' && opts.previous.mode === 'rule')
    ? opts.previous as ItemChangeRule : null
  const keep = prev ? readItemChange(prev, opts.self).keep : { fill: [], protect: [] }
  const fill: ItemChangeFillMapping[] = [...keep.fill]
  const protect: ItemChangeProtectMapping[] = [...keep.protect]
  for (const s of opts.sentences) {
    if (s.type === 'lock') protect.push({ form_col: s.target, when: compileLockWhen(opts.self, s.value) })
    else fill.push(compileFill(opts.self, s))
  }
  if (!fill.length && !protect.length) return null
  const lookup = typeof opts.lookup === 'string' ? parseLookup(opts.lookup) : opts.lookup
  const rule: ItemChangeRule = { ...(prev || {}), mode: 'rule', source_table: prev?.source_table || '', fill }
  if (protect.length) rule.protect = protect
  else delete rule.protect
  if (lookup && fill.some((f) => (f.table_col || '').trim())) {
    rule.source_table = lookup.table
    rule.match_table_col = lookup.data
    rule.match_mode = rule.match_mode || 'simple'
  }
  return rule
}

export function compileFill(self: string, s: Exclude<IcSentence, { type: 'lock' }>): ItemChangeFillMapping {
  switch (s.type) {
    case 'fill': return { form_col: s.target, table_col: s.source }
    case 'set': return { form_col: s.target, expr: quoteText(s.value) }
    case 'clear': return { form_col: s.target, expr: "''" }
    case 'calc': {
      const other = s.field ? ref(s.field) : String(Number(s.number ?? 0))
      return { form_col: s.target, expr: `${ref(self)} ${s.op} ${other}` }
    }
  }
}

export function compileLockWhen(self: string, value: string): string {
  return `${ref(self)} == ${quoteText(value)}`
}

/** The sentence in words. `label(name)` gives a field's name on the form;
 *  `source(name)` a column's name on the picked-from screen. */
export function icText(s: IcSentence, self: string, label: (n: string) => string,
  source: (n: string) => string = label, from = 'the picked record'): string {
  switch (s.type) {
    case 'fill': return `Fill in ${label(s.target)} with ${source(s.source)} from ${from}`
    case 'calc': {
      const op = CALC_OPS.find((o) => o.op === s.op)?.label ?? s.op
      return `Set ${label(s.target)} to ${label(self)} ${op} ${s.field ? label(s.field) : s.number}`
    }
    case 'set': return `Set ${label(s.target)} to “${s.value}”`
    case 'clear': return `Clear ${label(s.target)}`
    case 'lock': return `Lock ${label(s.target)} when ${label(self)} is “${s.value}”`
  }
}
